import React from "react";
import { Popover, Box, Typography, Button } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import {
  Videocam as VideocamIcon,
  Hotel as HotelIcon,
  TheaterComedy as TheaterIcon,
  Park as ParkIcon,
  Business as BusinessIcon,
  Place as PlaceIcon,
} from "@mui/icons-material";

export interface LocationOption {
  id: string;
  name: string;
  description: string;
  icon: SvgIconComponent;
  iconBg: string;
  iconColor: string;
}

interface SponsorshipLocationPickerProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  value: string;
  onChange: (location: string) => void;
  locations?: LocationOption[];
  title?: string;
}

const DEFAULT_LOCATIONS: LocationOption[] = [
  {
    id: "broadcast-studio",
    name: "Broadcast Studio",
    description: "Professional soundproof facility with lighting grids",
    icon: VideocamIcon,
    iconBg: "#DBEAFE",
    iconColor: "#2563EB",
  },
  {
    id: "hotel",
    name: "Hotel",
    description: "Upscale corporate setting for live audiences",
    icon: HotelIcon,
    iconBg: "#F3E8FF",
    iconColor: "#9333EA",
  },
  {
    id: "theatre",
    name: "Theatre",
    description: "Tiered seating for high-impact stage productions",
    icon: TheaterIcon,
    iconBg: "#FFE4E6",
    iconColor: "#E11D48",
  },
  {
    id: "outdoor-garden",
    name: "Outdoor Garden",
    description: "Natural light setting for lifestyle segments",
    icon: ParkIcon,
    iconBg: "#DCFCE7",
    iconColor: "#16A34A",
  },
  {
    id: "corporate-hub",
    name: "Innovation Hub",
    description: "Modern, tech-focused space for business talks",
    icon: BusinessIcon,
    iconBg: "#FEF3C7",
    iconColor: "#D97706",
  },
  {
    id: "custom",
    name: "Other Venue",
    description: "Bespoke or pop-up filming location",
    icon: PlaceIcon,
    iconBg: "#F3F4F6",
    iconColor: "#4B5563",
  },
];

const SponsorshipLocationPicker: React.FC<SponsorshipLocationPickerProps> = ({
  open,
  anchorEl,
  onClose,
  value,
  onChange,
  locations = DEFAULT_LOCATIONS,
  title = "Select venue type",
}) => {
  const handleLocationClick = (locationName: string) => {
    onChange(locationName);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, md: 4 },
          p: { xs: 2, md: 2.5 },
          width: { xs: 320, md: 380 },
          maxHeight: { xs: "100%", lg: 400 },
          mt: 1,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontSize: { xs: "0.938rem", md: "1rem" },
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {locations.map((location) => {
          const Icon = location.icon;
          const isSelected = value === location.name;

          return (
            <Button
              key={location.id}
              onClick={() => handleLocationClick(location.name)}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                textTransform: "none",
                justifyContent: "flex-start",
                bgcolor: isSelected ? "grey.100" : "transparent",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "grey.50",
                },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  bgcolor: location.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon sx={{ fontSize: 24, color: location.iconColor }} />
              </Box>
              <Box sx={{ flex: 1, textAlign: "left" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                >
                  {location.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                  }}
                >
                  {location.description}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Box>
    </Popover>
  );
};

export default SponsorshipLocationPicker;
